import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { toast } from 'react-hot-toast'

import { useStore } from 'hooks/useStore'
import { useProfileCompletionRequired } from 'hooks/useProfileCompletionRequired'
import { ContentModal } from 'components/organisms/modals/content-modal'
import { TermsOfServiceAndPrivacyBlock } from 'containers/common'
import { useAgreeToTermsAndConditionsMutation } from 'graphql/generated/scheme'
import { trueErrorMessage } from 'utils/error'
import { Button } from 'components/atoms/button'

export function UpdateTermsOfServiceAndPrivacyModal() {
	const { refetchUser, currentTerms, loadingUser, user } = useStore()
	const profileCompletionRequired = useProfileCompletionRequired()
	const [openUpdateTermsModal, setOpenUpdateTermsModal] = useState<boolean>(false)
	const [hasShownModal, setHasShownModal] = useState<boolean>(false)
	const [agreeToTermsAndConditions, { loading: loadingAgreeToTermsAndConditions }] =
		useAgreeToTermsAndConditionsMutation({
			onCompleted: () => {
				refetchUser()
				setOpenUpdateTermsModal(false)
			},
			onError: error => {
				toast.error(trueErrorMessage(error))
			},
		})

	useEffect(() => {
		// Reset "already shown" when user has agreed, so we can show again if terms change later
		if (user?.agreeToTermsAndConditions === true) {
			setHasShownModal(false)
		}
	}, [user?.agreeToTermsAndConditions])

	useEffect(() => {
		// A stale profile can open this before the completion modal is known to be required: yield to it and
		// offer the terms again once the profile is complete.
		if (profileCompletionRequired) {
			setOpenUpdateTermsModal(false)
			setHasShownModal(false)
		}
	}, [profileCompletionRequired])

	useEffect(() => {
		if (!loadingUser && user && !user.agreeToTermsAndConditions && !profileCompletionRequired && !hasShownModal) {
			setOpenUpdateTermsModal(true)
			setHasShownModal(true)
		}
	}, [loadingUser, user, profileCompletionRequired, hasShownModal])

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		const { version } = currentTerms
		agreeToTermsAndConditions({ variables: { version } })
	}

	return (
		<ContentModal title="There's something new!" open={openUpdateTermsModal} hideCloseButton>
			<div className="space-y-5 text-left">
				<div>We've updated Our Terms of Service and Privacy Policy. Please read and confirm!</div>
				<div>
					<form onSubmit={onSubmit}>
						<div className="space-y-4">
							<TermsOfServiceAndPrivacyBlock
								label="By clicking update, you agree to #termsOfService and #privacyPolicy."
								params={{
									termsOfService: 'Our Terms of Service',
									privacyPolicy: 'Privacy Policy',
								}}
							/>
						</div>
						<div className="space-y-2 mt-8">
							<Button
								type="submit"
								variant="primary"
								fullWidth
								className="flex justify-center items-center gap-1"
								disabled={loadingAgreeToTermsAndConditions || loadingUser}
							>
								{loadingAgreeToTermsAndConditions && (
									<FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin />
								)}
								Update
							</Button>
						</div>
					</form>
				</div>
			</div>
		</ContentModal>
	)
}
